import { clearTokens, setTokens, type AuthTokens } from './tokenStore';
import { getAuthBaseUrl } from './getAuthBaseUrl';
import { fetchWithAuth } from './fetchWithAuth';

export type RegistrationRequest = {
  username: string;
  email: string;
  password: string;
  full_name?: string;
  avatar?: File | null;
};

export type LoginRequest = {
  username?: string;
  email?: string;
  password: string;
};

export type ApiUser = {
  username: string;
  email: string;
  full_name: string;
  avatar: string;
};

export type ApiErrorResponse = {
  error: string;
};

export class AuthApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'AuthApiError';
    this.status = status;
  }
}

export function isEmailNotVerifiedError(error: unknown): boolean {
  return (
    error instanceof AuthApiError &&
    error.status === 403 &&
    error.message === 'email is not verified'
  );
}

export type VerifyEmailResult =
  | {
      kind: 'session';
      tokens: AuthTokens;
      user: ApiUser;
    }
  | {
      kind: 'profile';
      user: ApiUser;
    };

export type ProfileUpdateRequest = {
  username?: string;
  email?: string;
  full_name?: string;
  avatar?: File | null;
};

export type AccountExistsUser = {
  id: number;
  username: string;
  email: string;
  full_name: string;
  avatar: string;
};

async function readErrorMessage(resp: Response): Promise<string> {
  try {
    const data = (await resp.json()) as Partial<ApiErrorResponse>;

    if (typeof data?.error === 'string' && data.error) {
      return data.error;
    }
  } catch {}

  return `${resp.status} ${resp.statusText}`.trim();
}

async function throwAuthError(resp: Response): Promise<never> {
  throw new AuthApiError(await readErrorMessage(resp), resp.status);
}

function userFromAuthPayload(data: Record<string, unknown>): ApiUser {
  const nested = data.user;
  const source =
    nested && typeof nested === 'object' ? (nested as Record<string, unknown>) : data;
  return {
    username: typeof source.username === 'string' ? source.username : '',
    email: typeof source.email === 'string' ? source.email : '',
    full_name: typeof source.full_name === 'string' ? source.full_name : '',
    avatar: typeof source.avatar === 'string' ? source.avatar : '',
  };
}

function tokensFromAuthPayload(data: Record<string, unknown>): AuthTokens | null {
  if (typeof data.access_token !== 'string' || typeof data.refresh_token !== 'string') {
    return null;
  }
  const expiresIn = typeof data.expires_in === 'number' ? data.expires_in : 0;
  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + expiresIn * 1000,
  };
}

export async function registerUser(payload: RegistrationRequest): Promise<void> {
  const url = `${getAuthBaseUrl()}/auth/v1/registration`;

  if (payload.avatar) {
    const form = new FormData();
    form.append('username', payload.username);
    form.append('email', payload.email);
    form.append('password', payload.password);
    const fullName = payload.full_name?.trim();
    if (fullName) {
      form.append('full_name', fullName);
    }
    form.append('avatar', payload.avatar);
    const resp = await fetch(url, { method: 'POST', body: form });
    if (!resp.ok) {
      await throwAuthError(resp);
    }
    return;
  }

  const body: Record<string, string> = {
    username: payload.username,
    email: payload.email,
    password: payload.password,
  };
  const fullName = payload.full_name?.trim();
  if (fullName) {
    body.full_name = fullName;
  }

  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!resp.ok) {
    await throwAuthError(resp);
  }
}

export async function loginUser(payload: LoginRequest): Promise<{
  tokens: AuthTokens;
  user: Pick<ApiUser, 'username' | 'full_name' | 'avatar'>;
}> {
  const resp = await fetch(`${getAuthBaseUrl()}/auth/v1/login`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!resp.ok) {
    await throwAuthError(resp);
  }

  const data = (await resp.json()) as Record<string, unknown>;
  const tokens = tokensFromAuthPayload(data);
  if (!tokens) {
    throw new AuthApiError('Не удалось войти', resp.status);
  }
  setTokens(tokens);
  const user = userFromAuthPayload(data);

  return {
    tokens,
    user: {
      username: user.username,
      full_name: user.full_name,
      avatar: user.avatar,
    },
  };
}

export async function refreshTokens(refreshToken: string): Promise<AuthTokens> {
  const resp = await fetch(`${getAuthBaseUrl()}/auth/v1/refresh`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!resp.ok) {
    await throwAuthError(resp);
  }

  const data = (await resp.json()) as Record<string, unknown>;
  const tokens = tokensFromAuthPayload(data);
  if (!tokens) {
    throw new AuthApiError('Не удалось обновить сессию', resp.status);
  }
  setTokens(tokens);
  return tokens;
}

const verifyInflight = new Map<string, Promise<VerifyEmailResult>>();

async function requestEmailVerification(token: string): Promise<VerifyEmailResult> {
  const resp = await fetch(`${getAuthBaseUrl()}/auth/v1/email/verify`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  if (!resp.ok) {
    await throwAuthError(resp);
  }

  const data = (await resp.json()) as Record<string, unknown>;
  const user = userFromAuthPayload(data);
  const tokens = tokensFromAuthPayload(data);
  if (tokens) {
    setTokens(tokens);
    return { kind: 'session', tokens, user };
  }
  return { kind: 'profile', user };
}

export function verifyEmail(token: string): Promise<VerifyEmailResult> {
  const existing = verifyInflight.get(token);
  if (existing) return existing;

  const request = requestEmailVerification(token).finally(() => {
    verifyInflight.delete(token);
  });
  verifyInflight.set(token, request);
  return request;
}

export async function resendEmailVerification(email: string): Promise<void> {
  const resp = await fetch(`${getAuthBaseUrl()}/auth/v1/email/verify/resend`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!resp.ok) {
    await throwAuthError(resp);
  }
}

export async function logoutUser(refreshToken: string): Promise<void> {
  const resp = await fetch(`${getAuthBaseUrl()}/auth/v1/logout`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!resp.ok) {
    clearTokens();
    await throwAuthError(resp);
  }
  clearTokens();
}

export async function requestPasswordReset(email: string): Promise<void> {
  const resp = await fetch(`${getAuthBaseUrl()}/auth/v1/password/forgot`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!resp.ok) {
    await throwAuthError(resp);
  }
}

export async function resetPassword(token: string, password: string): Promise<void> {
  const resp = await fetch(`${getAuthBaseUrl()}/auth/v1/password/reset`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });

  if (!resp.ok) {
    await throwAuthError(resp);
  }
}

export async function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  const resp = await fetchWithAuth(`${getAuthBaseUrl()}/auth/v1/password/change`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
    redirectOnUnauthorized: true,
  });

  if (!resp.ok) {
    await throwAuthError(resp);
  }
}

export async function getProfile(): Promise<ApiUser> {
  const resp = await fetchWithAuth(`${getAuthBaseUrl()}/auth/v1/profile`, {
    method: 'GET',
    redirectOnUnauthorized: true,
  });

  if (!resp.ok) {
    await throwAuthError(resp);
  }

  const data = (await resp.json()) as { user: ApiUser };
  return {
    username: data.user?.username ?? '',
    email: data.user?.email ?? '',
    full_name: data.user?.full_name ?? '',
    avatar: data.user?.avatar ?? '',
  };
}

export async function updateProfile(payload: ProfileUpdateRequest): Promise<ApiUser> {
  const url = `${getAuthBaseUrl()}/auth/v1/profile`;

  let resp: Response;
  if (payload.avatar) {
    const form = new FormData();
    if (payload.username !== undefined) form.append('username', payload.username);
    if (payload.email !== undefined) form.append('email', payload.email);
    if (payload.full_name !== undefined) form.append('full_name', payload.full_name);
    form.append('avatar', payload.avatar);
    resp = await fetchWithAuth(url, {
      method: 'PATCH',
      body: form,
      redirectOnUnauthorized: true,
    });
  } else {
    const body: Record<string, string> = {};
    if (payload.username !== undefined) body.username = payload.username;
    if (payload.email !== undefined) body.email = payload.email;
    if (payload.full_name !== undefined) body.full_name = payload.full_name;
    resp = await fetchWithAuth(url, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      redirectOnUnauthorized: true,
    });
  }

  if (!resp.ok) {
    await throwAuthError(resp);
  }

  const data = (await resp.json()) as { user: ApiUser };
  return {
    username: data.user?.username ?? '',
    email: data.user?.email ?? '',
    full_name: data.user?.full_name ?? '',
    avatar: data.user?.avatar ?? '',
  };
}

export async function accountExists(params: {
  id?: number;
  email?: string;
  username?: string;
}): Promise<{ exists: boolean; user?: AccountExistsUser }> {
  const search = new URLSearchParams();
  if (params.id !== undefined) search.set('id', String(params.id));
  if (params.email) search.set('email', params.email);
  if (params.username) search.set('username', params.username);

  const resp = await fetchWithAuth(
    `${getAuthBaseUrl()}/auth/v1/account/exists?${search.toString()}`,
    { method: 'GET', redirectOnUnauthorized: true },
  );

  if (!resp.ok) {
    await throwAuthError(resp);
  }

  return (await resp.json()) as { exists: boolean; user?: AccountExistsUser };
}
